import React, { useState, useEffect, useRef } from 'react';
import { StageBlueprint, Venue, VenuePackage } from '../../types';
import { ConicGlowCard } from '../common/ConicGlowCard';
import { runStageViewTransition } from '../../utils/viewTransitions';
import {
  MapPinIcon,
  StarIcon,
  ShieldCheckIcon,
  GlobeIcon,
  PhoneIcon,
  CameraIcon,
  CalendarIcon,
  CloseIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  SparklesIcon,
  CheckIcon,
  UsersIcon,
  ZapIcon
} from '../common/Icons';

export type ShowcaseFocusView = 'overview' | 'packages' | 'photos' | 'amenities' | 'custom_focus';

interface VenueShowcaseDeckProps {
  venue?: Venue | null;
  focusView?: ShowcaseFocusView;
  initialPhotoCategory?: 'all' | 'hall' | 'stage' | 'dining' | 'lawn' | 'entrance' | 'bridal_room' | 'general';
  initialGuestCount?: number;
  focusMetrics?: string[];
  blueprint?: StageBlueprint | null;
  onCheckAvailability: (venue: Venue, selectedPackage?: VenuePackage) => void;
  onCompareWithOthers?: (venue: Venue) => void;
  onBackToSpotlight?: () => void;
  onTouchAction?: (action: string, data?: any) => void;
}

export const VenueShowcaseDeck: React.FC<VenueShowcaseDeckProps> = React.memo(({
  venue,
  focusView = 'overview',
  initialPhotoCategory = 'all',
  focusMetrics = [],
  onCheckAvailability,
  onBackToSpotlight,
  onTouchAction
}) => {
  const [activeCardView, setActiveCardView] = useState<ShowcaseFocusView>(
    focusView === 'packages' ? 'overview' : focusView || 'overview'
  );
  const [activeCategory, setActiveCategory] = useState<'all' | 'hall' | 'stage' | 'dining' | 'lawn' | 'entrance' | 'bridal_room' | 'general'>(initialPhotoCategory);
  const [activePhotoIndex, setActivePhotoIndex] = useState<number>(0);
  const [lightboxItem, setLightboxItem] = useState<{ label: string; source: string; desc: string; imgUrl: string } | null>(null);
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const pointerStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const suppressNextClickRef = useRef<boolean>(false);

  useEffect(() => {
    if (focusView) {
      setActiveCardView(focusView === 'packages' ? 'overview' : focusView);
    }
    setActivePhotoIndex(0);
  }, [focusView, venue?.id]);

  useEffect(() => {
    if (initialPhotoCategory) {
      setActiveCategory(initialPhotoCategory);
    } else {
      setActiveCategory('all');
    }
    setActivePhotoIndex(0);
  }, [initialPhotoCategory, venue?.id]);

  const defaultVenue: Venue = {
    id: 'v-karsaz-01',
    slug: 'maritime-convention-centre-karsaz',
    name: 'Maritime Convention & Banquet',
    area: 'Karsaz',
    address: 'Habib Ibrahim Rehmatullah Road, Karsaz, Karachi',
    lat: 24.8821,
    lng: 67.0892,
    venueType: 'banquet_hall',
    minCapacity: 300,
    maxCapacity: 1200,
    googleRating: 4.8,
    googleReviewCount: 840,
    isClaimed: true,
    amenities: ['Pillarless Hall', 'Central Chiller AC', 'Standby Generator Backup', 'Valet Parking'],
    packages: [
      { id: 'pkg-offpeak', packageType: 'flat_rental_offpeak', name: 'Off-Peak / Weekday Hall Rental', pricePkr: 325000, minGuests: 150, menuItems: ['Central Air Conditioning', 'Standby Generator Backup', 'Valet Parking'] },
      { id: 'pkg-peak', packageType: 'flat_rental_peak', name: 'Peak Season Hall Rental', pricePkr: 450000, minGuests: 150, menuItems: ['Central Air Conditioning', 'Standby Generator Backup', 'Stage Setup', 'Valet Parking'] }
    ]
  };

  const currentVenue = venue || defaultVenue;
  const rawPackages: VenuePackage[] =
    currentVenue.packages && currentVenue.packages.length > 0
      ? currentVenue.packages
      : defaultVenue.packages!;
  const venuePackages: VenuePackage[] = Array.from(
    new Map(rawPackages.map((p) => [`${p.packageType}:${p.name}`, p])).values()
  );

  const flatPeakPkg = venuePackages.find((p) => p.packageType === 'flat_rental_peak' || p.packageType === 'flat_hall_rental');
  const flatOffpeakPkg = venuePackages.find((p) => p.packageType === 'flat_rental_offpeak');
  const hasExplicitHallRental = Boolean(flatPeakPkg && !flatPeakPkg.isEstimated && currentVenue.isClaimed);
  const minPerHeadPkr = venuePackages.find((p) => !p.packageType.startsWith('flat'))?.pricePkr || 1850;
  const peakHallRentalPkr = flatPeakPkg ? flatPeakPkg.pricePkr : Math.round(minPerHeadPkr * 180);
  const offPeakHallRentalPkr = flatOffpeakPkg ? flatOffpeakPkg.pricePkr : Math.round(peakHallRentalPkr * 0.72);

  const formatVenueType = (t?: string) => {
    if (!t) return 'Banquet Hall';
    return t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Extract real visual & facility highlights from the venue's amenities array
  const cleanAmenityList: string[] = Array.isArray(currentVenue.amenities)
    ? currentVenue.amenities
        .map((a) => String(a).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()))
        .filter((a) => !/catering|food|dish|curfew/i.test(a))
    : [];

  const visualFeatures = cleanAmenityList.filter(
    (a) => !/air conditioning|generator|valet|parking|partition|bridal|security|lighting/i.test(a)
  );

  const hasGeneratorBackup = cleanAmenityList.some((a) => /gen|power|backup/i.test(a));
  const hasAirConditioning = cleanAmenityList.some((a) => /ac|air condition|chiller/i.test(a));
  const hasValetParking = cleanAmenityList.some((a) => /valet|parking/i.test(a));

  const resolveShowcaseMetric = (metric: string): string => {
    const m = metric.toLowerCase().replace(/_/g, ' ');
    if (m.includes('gen') || m.includes('power') || m.includes('backup')) {
      return 'Electricity & AC: Standby Generator + AC Backup';
    }
    if (m.includes('valet') || m.includes('parking')) {
      return 'Parking: Dedicated Valet Parking';
    }
    if (m.includes('cap') || m.includes('guest') || m.includes('pax')) {
      return `Capacity: ${currentVenue.minCapacity}–${currentVenue.maxCapacity} Guests`;
    }
    if (m.includes('price') || m.includes('rate') || m.includes('rental')) {
      return `Peak Hall Rental: ${hasExplicitHallRental ? '' : '~'}Rs. ${peakHallRentalPkr.toLocaleString()}`;
    }
    return `${metric.replace(/_/g, ' ')}: Included`;
  };

  const rawCoverUrl =
    currentVenue.images?.[0]?.filePathWebp ||
    (currentVenue.images?.[0] as any)?.file_path_webp ||
    `/uploads/venues/${currentVenue.slug}/photo_0.webp`;
  const coverUrl = rawCoverUrl.includes('?') ? rawCoverUrl : `${rawCoverUrl}?v=2`;

  const describeCategorySpace = (cat: string, caption: string): string => {
    const featureNote =
      visualFeatures.length > 0
        ? visualFeatures.slice(0, 3).join(', ')
        : 'Central AC, standby generator backup, and valet parking';
    if (cat === 'stage') {
      return `${caption} at ${currentVenue.name} (${currentVenue.area}) for ${currentVenue.minCapacity}–${currentVenue.maxCapacity} guests.`;
    }
    if (cat === 'lawn') {
      return `${caption} in ${currentVenue.area} with open-air event layout for ${currentVenue.minCapacity}–${currentVenue.maxCapacity} guests.`;
    }
    if (cat === 'entrance') {
      return `${caption} with guest arrival access and valet parking in ${currentVenue.area}.`;
    }
    return `${caption} at ${currentVenue.name} (${currentVenue.area}) — ${featureNote}.`;
  };

  // Deduplicate verified venue images by URL so zero duplicate images ever render
  const uniqueVenueImages = (() => {
    const rawImgs = Array.isArray(currentVenue.images) ? currentVenue.images : [];
    const seenUrls = new Set<string>();
    const deduped: Array<{
      imgUrl: string;
      category: string;
      label: string;
      source: string;
      desc: string;
    }> = [];

    for (let i = 0; i < rawImgs.length; i++) {
      const img: any = rawImgs[i];
      const rawPath = img?.filePathWebp || img?.file_path_webp;
      if (!rawPath) continue;
      const cleanPath = String(rawPath).split('?')[0];
      if (seenUrls.has(cleanPath)) continue;
      seenUrls.add(cleanPath);

      const urlWithVer = String(rawPath).includes('?') ? String(rawPath) : `${rawPath}?v=2`;
      const cat = String(img?.category || (i === 0 ? 'hall' : i === 1 ? 'stage' : 'general')).toLowerCase();
      const label =
        img?.caption && String(img.caption).trim()
          ? String(img.caption).trim()
          : i === 0
            ? `${currentVenue.name} Main Hall`
            : `${currentVenue.name} Space View ${i + 1}`;
      const source =
        img?.source === 'owner_upload' || currentVenue.isClaimed
          ? 'Owner Verified'
          : 'Google Maps Photo';

      deduped.push({
        imgUrl: urlWithVer,
        category: cat,
        label,
        source,
        desc: describeCategorySpace(cat, label)
      });
    }

    if (deduped.length === 0) {
      deduped.push({
        imgUrl: coverUrl,
        category: currentVenue.venueType === 'lawn' ? 'lawn' : 'hall',
        label: `${currentVenue.name} Main Hall`,
        source: currentVenue.isClaimed ? 'Owner Verified' : 'Google Maps Photo',
        desc: describeCategorySpace('hall', `${currentVenue.name} Main Hall`)
      });
    }

    return deduped.slice(0, 6);
  })();

  const gallerySpaces = uniqueVenueImages;

  const categoryLabelsMap: Record<string, string> = {
    hall: 'Hall',
    stage: 'Stage',
    dining: 'Seating',
    lawn: 'Lawn',
    entrance: 'Arrival',
    bridal_room: 'VIP Suite',
    general: 'Space'
  };

  const presentCategories = Array.from(new Set(gallerySpaces.map((g) => g.category)));
  const categories = [
    { id: 'all', label: `All (${gallerySpaces.length})` },
    ...presentCategories.map((catId) => ({
      id: catId,
      label: categoryLabelsMap[catId] || catId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    }))
  ];

  const filteredGallery =
    activeCategory === 'all'
      ? gallerySpaces
      : gallerySpaces.filter((item) => item.category === activeCategory).length > 0
        ? gallerySpaces.filter((item) => item.category === activeCategory)
        : gallerySpaces;

  const placeIdParam = (currentVenue as any).google_place_id
    ? `&query_place_id=${encodeURIComponent((currentVenue as any).google_place_id)}`
    : '';
  const mapsSearchUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${currentVenue.name} ${currentVenue.address}`
  )}${placeIdParam}`;

  // Only show real phone number from Google Places (never dummy fallback '+92 21 34551000')
  const rawPhone = (currentVenue as any).phone || '';
  const verifiedPhone =
    rawPhone && rawPhone !== '+92 21 34551000' ? String(rawPhone).trim() : null;

  // Only show real website URL if present in database
  const verifiedWebsite =
    currentVenue.websiteUrl && /^https?:\/\//i.test(currentVenue.websiteUrl)
      ? currentVenue.websiteUrl
      : null;

  const safePhotoIndex = Math.min(Math.max(0, activePhotoIndex), Math.max(0, filteredGallery.length - 1));
  const activePhotoItem = filteredGallery[safePhotoIndex] || filteredGallery[0];

  const switchFocusView = (nextView: ShowcaseFocusView, targetPhotoIdx?: number) => {
    const transitionType = nextView === 'overview' ? 'card-collapse' : 'card-expand';
    runStageViewTransition(() => {
      setActiveCardView(nextView);
      if (typeof targetPhotoIdx === 'number') {
        setActivePhotoIndex(targetPhotoIdx);
      }
    }, transitionType);
    if (onTouchAction) {
      onTouchAction('switch_showcase_focus', {
        venueId: currentVenue.id,
        focusView: nextView,
        ...(typeof targetPhotoIdx === 'number' ? { photoIndex: targetPhotoIdx } : {})
      });
    }
  };

  // Unified forward step: Overview -> Photos[0] -> Photos[1..N-1] -> Specs (amenities)
  const stepShowcaseForward = () => {
    const effectiveView = activeCardView === 'custom_focus' || activeCardView === 'packages' ? 'overview' : activeCardView;
    if (effectiveView === 'overview') {
      switchFocusView('photos', 0);
      return;
    }
    if (effectiveView === 'photos') {
      if (safePhotoIndex < filteredGallery.length - 1) {
        const nextIdx = safePhotoIndex + 1;
        runStageViewTransition(() => {
          setActivePhotoIndex(nextIdx);
          if (lightboxItem && filteredGallery[nextIdx]) {
            setLightboxItem(filteredGallery[nextIdx]);
          }
        }, 'card-expand');
        if (onTouchAction) {
          onTouchAction('swipe_showcase_photo', {
            venueId: currentVenue.id,
            photoIndex: nextIdx,
            totalPhotos: filteredGallery.length
          });
        }
      } else {
        if (lightboxItem) setLightboxItem(null);
        switchFocusView('amenities');
      }
    }
  };

  // Unified backward step: Specs (amenities) -> Photos[N-1] -> ... -> Photos[0] -> Overview
  const stepShowcaseBackward = () => {
    const effectiveView = activeCardView === 'custom_focus' || activeCardView === 'packages' ? 'overview' : activeCardView;
    if (effectiveView === 'amenities') {
      const lastIdx = Math.max(0, filteredGallery.length - 1);
      switchFocusView('photos', lastIdx);
      return;
    }
    if (effectiveView === 'photos') {
      if (safePhotoIndex > 0) {
        const prevIdx = safePhotoIndex - 1;
        runStageViewTransition(() => {
          setActivePhotoIndex(prevIdx);
          if (lightboxItem && filteredGallery[prevIdx]) {
            setLightboxItem(filteredGallery[prevIdx]);
          }
        }, 'card-collapse');
        if (onTouchAction) {
          onTouchAction('swipe_showcase_photo', {
            venueId: currentVenue.id,
            photoIndex: prevIdx,
            totalPhotos: filteredGallery.length
          });
        }
      } else {
        if (lightboxItem) setLightboxItem(null);
        switchFocusView('overview');
      }
      return;
    }
    if (effectiveView === 'overview') {
      // On Overview (leftmost section), swiping right or left jumps into Photos at photo 0
      switchFocusView('photos', 0);
    }
  };

  const isInsideScrollableStrip = (target: EventTarget | null): boolean => {
    const el = target as HTMLElement | null;
    return Boolean(el?.closest?.('.corridor-filter-strip, .showcase-social-strip'));
  };

  const commitHorizontalSwipe = (deltaX: number, deltaY: number): boolean => {
    const SWIPE_THRESHOLD_PX = 36;
    // Require clear horizontal gesture so vertical scrolling is never hijacked
    if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) {
      return false;
    }

    suppressNextClickRef.current = true;
    setTimeout(() => {
      suppressNextClickRef.current = false;
    }, 260);

    const effectiveView = activeCardView === 'custom_focus' || activeCardView === 'packages' ? 'overview' : activeCardView;
    if (effectiveView === 'overview') {
      stepShowcaseForward();
    } else if (deltaX < 0) {
      stepShowcaseForward();
    } else {
      stepShowcaseBackward();
    }
    return true;
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (isInsideScrollableStrip(e.target)) {
      touchStartRef.current = null;
      return;
    }
    const touch = e.touches?.[0];
    if (!touch) return;
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const touch = e.changedTouches?.[0];
    if (!touch) return;

    commitHorizontalSwipe(touch.clientX - start.x, touch.clientY - start.y);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'touch' || e.button !== 0 || isInsideScrollableStrip(e.target)) {
      pointerStartRef.current = null;
      return;
    }
    pointerStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      time: Date.now()
    };
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'touch') return;
    const start = pointerStartRef.current;
    pointerStartRef.current = null;
    if (!start) return;

    commitHorizontalSwipe(e.clientX - start.x, e.clientY - start.y);
  };

  const handleDeckClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const viewTabs: { id: ShowcaseFocusView; label: string; count?: number }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'photos', label: 'Photos', count: gallerySpaces.length },
    { id: 'amenities', label: 'Specs', count: 3 }
  ];

  return (
    <div
      className="stage-deck-content venue-showcase-deck"
      style={{ touchAction: 'pan-y' }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onClickCapture={handleDeckClickCapture}
    >
      {/* Row 1: Clean Compact Header (Back Pill + Venue Title & Rating Subtitle + Check Dates CTA) */}
      <div
        className="deck-header showcase-header-row"
        style={{
          marginBottom: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'nowrap',
          gap: '0.4rem'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.42rem', minWidth: 0, flex: 1 }}>
          {onBackToSpotlight && (
            <button
              type="button"
              className="btn-secondary"
              onClick={onBackToSpotlight}
              style={{ padding: '0.28rem 0.56rem', fontSize: '0.7rem', gap: '0.28rem', flexShrink: 0 }}
            >
              <ArrowLeftIcon size={12} />
              <span>Back</span>
            </button>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2
              id="stage-active-heading"
              data-stage-heading="true"
              tabIndex={-1}
              className="deck-title"
              style={{
                margin: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {currentVenue.name}
            </h2>
            <div
              className="deck-subtitle num-tabular"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.22rem',
                marginTop: '0.08rem',
                fontSize: '0.67rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              <span style={{ color: 'hsl(var(--accent-gold))', fontWeight: 700, flexShrink: 0 }}>
                {currentVenue.area}
              </span>
              <span>•</span>
              <StarIcon size={10} style={{ color: 'hsl(var(--accent-gold))', flexShrink: 0 }} />
              <span style={{ fontWeight: 600, color: 'hsl(var(--text-primary))' }}>
                {currentVenue.googleRating.toFixed(1)} ({currentVenue.googleReviewCount.toLocaleString()})
              </span>
              {currentVenue.isClaimed && (
                <>
                  <span>•</span>
                  <span style={{ color: 'hsl(var(--accent-emerald-text))', fontWeight: 700 }}>Verified</span>
                </>
              )}
            </div>
          </div>
        </div>

        <button
          type="button"
          className="btn-primary"
          style={{ padding: '0.32rem 0.65rem', fontSize: '0.7rem', gap: '0.28rem', flexShrink: 0, whiteSpace: 'nowrap' }}
          onClick={() => {
            onCheckAvailability(currentVenue, flatPeakPkg || venuePackages[0]);
          }}
        >
          <CalendarIcon size={12} />
          <span>Check Dates</span>
        </button>
      </div>

      {/* Row 2: Compact Segmented 3-Tab Sub-Navigation (Zero Horizontal Clipping) */}
      <div
        className="showcase-subnav-bar"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.4rem',
          marginBottom: '0.65rem'
        }}
      >
        <div
          className="corridor-filter-strip"
          style={{ marginBottom: 0, padding: '0.06rem 0', gap: '0.32rem', width: '100%' }}
          role="tablist"
          aria-label="Focused Venue Card Views"
        >
          {viewTabs.map((tab) => {
            const isActive =
              activeCardView === tab.id ||
              (activeCardView === 'custom_focus' && tab.id === 'overview');
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`corridor-chip ${isActive ? 'active' : ''}`}
                style={{ padding: '0.26rem 0.64rem', fontSize: '0.72rem' }}
                onClick={() => switchFocusView(tab.id, tab.id === 'photos' ? 0 : undefined)}
              >
                <SparklesIcon size={11} />
                <span>
                  {tab.label}
                  {tab.count !== undefined ? ` (${tab.count})` : ''}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ====================================================================
          VIEW 1: DEDICATED SPACE & ARCHITECTURAL PHOTO CARDS + SWIPE CAROUSEL
          ==================================================================== */}
      {activeCardView === 'photos' && (
        <div>
          <div className="corridor-filter-strip" style={{ marginBottom: '0.55rem', gap: '0.28rem', padding: '0.05rem 0' }}>
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                className={`corridor-chip ${activeCategory === cat.id ? 'active' : ''}`}
                style={{ padding: '0.2rem 0.52rem', fontSize: '0.68rem', gap: '0.22rem' }}
                onClick={() => {
                  runStageViewTransition(() => {
                    setActiveCategory(cat.id as any);
                    setActivePhotoIndex(0);
                  });
                  if (onTouchAction) onTouchAction('filter_photos', { category: cat.id });
                }}
              >
                <CameraIcon size={11} />
                <span>{cat.label}</span>
              </button>
            ))}
          </div>

          {/* Interactive Swipeable Active Photo Stage (Swipe Left/Right Steps 0..N-1 & Jumps Across Sections) */}
          {activePhotoItem && (
            <div
              className="showcase-photo-carousel-stage"
              role="region"
              aria-label={`Active photo ${safePhotoIndex + 1} of ${filteredGallery.length}: ${activePhotoItem.label}`}
              style={{
                position: 'relative',
                height: '13.5rem',
                borderRadius: '1rem',
                overflow: 'hidden',
                marginBottom: '0.7rem',
                border: '1px solid hsl(var(--glow-gemini-cyan) / 0.45)',
                background: 'linear-gradient(135deg, hsl(var(--obsidian-850)), hsl(var(--obsidian-950)))',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                padding: '0.75rem 0.85rem',
                touchAction: 'pan-y'
              }}
            >
              <img
                key={activePhotoItem.imgUrl}
                src={activePhotoItem.imgUrl}
                alt={`${currentVenue.name} - ${activePhotoItem.label}`}
                loading="eager"
                decoding="async"
                draggable={false}
                onClick={() => setLightboxItem(activePhotoItem)}
                onError={(e) => {
                  const imgEl = e.currentTarget as HTMLImageElement;
                  if (imgEl.src !== window.location.origin + coverUrl && !imgEl.dataset.fallbackApplied) {
                    imgEl.dataset.fallbackApplied = 'true';
                    imgEl.src = coverUrl;
                  } else {
                    imgEl.style.display = 'none';
                  }
                }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  cursor: 'pointer',
                  zIndex: 0
                }}
              />
              <div className="venue-card-scrim" style={{ zIndex: 1, pointerEvents: 'none' }} />

              {/* Top Overlay: Photo Counter + Expand & Verification Pill */}
              <div style={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.35rem' }}>
                <span className="pill-badge gemini num-tabular" style={{ fontSize: '0.64rem', padding: '0.16rem 0.5rem', fontWeight: 700 }}>
                  <CameraIcon size={10} />
                  <span>
                    Photo {safePhotoIndex + 1} of {filteredGallery.length}
                  </span>
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <button
                    type="button"
                    className="pill-badge"
                    onClick={() => setLightboxItem(activePhotoItem)}
                    style={{ fontSize: '0.62rem', padding: '0.14rem 0.46rem', cursor: 'pointer', border: '1px solid hsl(var(--porcelain-50) / 0.2)' }}
                  >
                    <CameraIcon size={10} />
                    <span>Expand</span>
                  </button>
                  <span
                    className={`pill-badge ${activePhotoItem.source === 'Owner Verified' ? 'verified' : ''}`}
                    style={{ fontSize: '0.62rem', padding: '0.14rem 0.44rem' }}
                  >
                    {activePhotoItem.source === 'Owner Verified' ? 'Verified' : 'Maps Photo'}
                  </span>
                </div>
              </div>

              {/* Middle Overlay: Prev & Next Stepper Controls (Also Steps Across Overview / Specs Boundary) */}
              <div style={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.4rem' }}>
                <button
                  type="button"
                  className="btn-secondary showcase-carousel-nav-btn"
                  onClick={stepShowcaseBackward}
                  aria-label={safePhotoIndex === 0 ? 'Return to Overview section' : `Previous photo (${safePhotoIndex} of ${filteredGallery.length})`}
                  style={{ padding: '0.26rem 0.54rem', fontSize: '0.66rem', gap: '0.24rem' }}
                >
                  <ArrowLeftIcon size={11} />
                  <span>{safePhotoIndex === 0 ? 'Overview' : 'Prev'}</span>
                </button>
                <button
                  type="button"
                  className="btn-secondary showcase-carousel-nav-btn"
                  onClick={stepShowcaseForward}
                  aria-label={safePhotoIndex >= filteredGallery.length - 1 ? 'Advance to Specs section' : `Next photo (${safePhotoIndex + 2} of ${filteredGallery.length})`}
                  style={{ padding: '0.26rem 0.54rem', fontSize: '0.66rem', gap: '0.24rem' }}
                >
                  <span>{safePhotoIndex >= filteredGallery.length - 1 ? 'Specs' : 'Next'}</span>
                  <ArrowRightIcon size={11} />
                </button>
              </div>

              {/* Bottom Overlay: Active Photo Caption + Interactive Progress Dots */}
              <div style={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '0.5rem' }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <h4 style={{ fontWeight: 800, fontSize: '0.94rem', color: 'hsl(var(--porcelain-50))', marginBottom: '0.08rem', lineHeight: 1.2 }}>
                    {activePhotoItem.label}
                  </h4>
                  <p style={{ fontSize: '0.7rem', color: 'hsl(var(--porcelain-200))', lineHeight: 1.28, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {activePhotoItem.desc}
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.26rem', flexShrink: 0 }} role="tablist" aria-label="Gallery Photo Progress">
                  {filteredGallery.map((gItem, dotIdx) => (
                    <button
                      key={dotIdx}
                      type="button"
                      role="tab"
                      aria-selected={dotIdx === safePhotoIndex}
                      aria-label={`Jump to photo ${dotIdx + 1}: ${gItem.label}`}
                      onClick={() => setActivePhotoIndex(dotIdx)}
                      style={{
                        width: dotIdx === safePhotoIndex ? '1.15rem' : '0.42rem',
                        height: '0.42rem',
                        borderRadius: '9999px',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        backgroundColor:
                          dotIdx === safePhotoIndex
                            ? 'hsl(var(--glow-gemini-cyan))'
                            : 'hsl(var(--porcelain-50) / 0.35)',
                        transition: 'width 0.25s ease, background-color 0.25s ease'
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          <div
            className="grid-cards showcase-photos-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(18rem, 1fr))',
              gap: '0.85rem'
            }}
          >
            {filteredGallery.map((item, idx) => (
              <ConicGlowCard
                key={idx}
                className={`composable-stage-card ${idx === safePhotoIndex ? 'active-glow' : ''}`.trim()}
                glowIntensity={idx === safePhotoIndex ? 0.82 : 0.55}
                ariaLabel={`Select or expand photo: ${item.label}`}
                onClick={() => {
                  setActivePhotoIndex(idx);
                  setLightboxItem(item);
                }}
              >
                <div
                  className="showcase-photo-card-inner"
                  style={{
                    position: 'relative',
                    height: '13.5rem',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: '0.95rem',
                    overflow: 'hidden',
                    background: 'linear-gradient(135deg, hsl(var(--obsidian-850)), hsl(var(--obsidian-950)))'
                  }}
                >
                  <img
                    src={item.imgUrl}
                    alt={`${currentVenue.name} - ${item.label}`}
                    loading="eager"
                    decoding="async"
                    onError={(e) => {
                      const imgEl = e.currentTarget as HTMLImageElement;
                      if (imgEl.src !== window.location.origin + coverUrl && !imgEl.dataset.fallbackApplied) {
                        imgEl.dataset.fallbackApplied = 'true';
                        imgEl.src = coverUrl;
                      } else {
                        imgEl.style.display = 'none';
                      }
                    }}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      zIndex: 0
                    }}
                  />
                  <div className="venue-card-scrim" style={{ zIndex: 1 }} />

                  <div style={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.25rem' }}>
                    <span className={`pill-badge ${idx === safePhotoIndex ? 'gemini' : ''}`.trim()} style={{ fontSize: '0.62rem', padding: '0.14rem 0.42rem' }}>
                      <CameraIcon size={10} />
                      <span>{idx === safePhotoIndex ? `Active (${idx + 1}/${filteredGallery.length})` : 'Expand'}</span>
                    </span>
                    <span
                      className={`pill-badge ${item.source === 'Owner Verified' ? 'verified' : ''}`}
                      style={{ fontSize: '0.62rem', padding: '0.14rem 0.42rem' }}
                    >
                      {item.source === 'Owner Verified' ? 'Verified' : 'Maps Photo'}
                    </span>
                  </div>

                  <div style={{ position: 'relative', zIndex: 2 }}>
                    <h4 className="showcase-photo-title" style={{ fontWeight: 800, fontSize: '0.92rem', color: 'hsl(var(--porcelain-50))', marginBottom: '0.12rem', lineHeight: 1.22 }}>
                      {item.label}
                    </h4>
                    <p className="showcase-photo-desc" style={{ fontSize: '0.72rem', color: 'hsl(var(--porcelain-200))', lineHeight: 1.32, margin: 0 }}>
                      {item.desc}
                    </p>
                  </div>
                </div>
              </ConicGlowCard>
            ))}
          </div>
        </div>
      )}

      {/* ====================================================================
          VIEW 2: CAPACITY, ELECTRICITY & AC BACKUP, AND VALET PARKING CARDS
          ==================================================================== */}
      {activeCardView === 'amenities' && (
        <div
          className="grid-cards showcase-specs-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(19rem, 1fr))',
            gap: '0.9rem'
          }}
        >
          <ConicGlowCard
            className="composable-stage-card"
            glowIntensity={0.55}
            ariaLabel={`Check guest capacity & slot availability for ${currentVenue.name}`}
            onClick={() => onCheckAvailability(currentVenue, flatPeakPkg || venuePackages[0])}
          >
            <div className="showcase-spec-card-inner" style={{ padding: '1rem 1.05rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.38rem', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: 'hsl(var(--glow-gemini-blue))', marginBottom: '0.22rem' }}>
                <UsersIcon size={13} />
                <span>1. Guest Capacity &amp; Space Layout</span>
              </div>
              <div className="num-tabular showcase-spec-card-value" style={{ fontSize: '1.15rem', fontWeight: 800, marginBottom: '0.25rem' }}>
                {currentVenue.minCapacity} – {currentVenue.maxCapacity} Guests
              </div>
              <p className="showcase-spec-card-desc" style={{ fontSize: '0.75rem', color: 'hsl(var(--text-secondary))', lineHeight: 1.4, margin: 0 }}>
                Configured as a {formatVenueType(currentVenue.venueType).toLowerCase()} in {currentVenue.area} accommodating {currentVenue.minCapacity} to {currentVenue.maxCapacity} guests with flexible seating and stage sightlines.
              </p>
            </div>
          </ConicGlowCard>

          <ConicGlowCard
            className="composable-stage-card"
            glowIntensity={0.55}
            ariaLabel={`Check electricity & AC backup for ${currentVenue.name}`}
            onClick={() => onCheckAvailability(currentVenue, flatPeakPkg || venuePackages[0])}
          >
            <div className="showcase-spec-card-inner" style={{ padding: '1rem 1.05rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.38rem', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: 'hsl(var(--accent-emerald))', marginBottom: '0.22rem' }}>
                <ZapIcon size={13} />
                <span>2. Electricity &amp; AC Backup</span>
              </div>
              <div className="showcase-spec-card-value" style={{ fontSize: '1.15rem', fontWeight: 800, marginBottom: '0.25rem' }}>
                {hasGeneratorBackup && hasAirConditioning
                  ? 'Standby Generator + AC'
                  : 'Generator & Climate Backup'}
              </div>
              <p className="showcase-spec-card-desc" style={{ fontSize: '0.75rem', color: 'hsl(var(--text-secondary))', lineHeight: 1.4, margin: 0 }}>
                Dedicated standby generator backup for uninterrupted hall lighting, stage sound, and central air conditioning throughout your event slot.
              </p>
            </div>
          </ConicGlowCard>

          <ConicGlowCard
            className="composable-stage-card"
            glowIntensity={0.55}
            ariaLabel={`Check valet parking for ${currentVenue.name}`}
            onClick={() => onCheckAvailability(currentVenue, flatPeakPkg || venuePackages[0])}
          >
            <div className="showcase-spec-card-inner" style={{ padding: '1rem 1.05rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.38rem', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: 'hsl(var(--accent-gold))', marginBottom: '0.22rem' }}>
                <MapPinIcon size={13} />
                <span>3. Valet Parking &amp; Arrival</span>
              </div>
              <div className="showcase-spec-card-value" style={{ fontSize: '1.15rem', fontWeight: 800, marginBottom: '0.25rem' }}>
                {hasValetParking ? 'Valet Parking Available' : 'On-Site Parking & Drop-Off'}
              </div>
              <p className="showcase-spec-card-desc" style={{ fontSize: '0.75rem', color: 'hsl(var(--text-secondary))', lineHeight: 1.4, margin: 0 }}>
                Direct entrance drop-off along {currentVenue.area} with coordinated valet parking and guest arrival management.
              </p>
            </div>
          </ConicGlowCard>
        </div>
      )}

      {/* ====================================================================
          VIEW 3: 3-CARD EXECUTIVE OVERVIEW (Hall Rental, Photos, Core Specs)
          ==================================================================== */}
      {(activeCardView === 'overview' || activeCardView === 'custom_focus') && (
        <div>
          {focusMetrics && focusMetrics.length > 0 && (
            <div className="relaxation-banner" style={{ marginBottom: '0.55rem', padding: '0.38rem 0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                <SparklesIcon size={13} style={{ color: 'hsl(var(--accent-gold))' }} />
                <strong>Focused Specs:</strong>
                {focusMetrics.map((m, idx) => (
                  <span key={idx} className="pill-badge verified" style={{ fontSize: '0.65rem', padding: '0.12rem 0.45rem' }}>
                    {resolveShowcaseMetric(m)}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div
            className="grid-cards showcase-overview-bento"
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(21rem, 1fr))',
              gap: '0.95rem'
            }}
          >
            {/* EXECUTIVE CARD 1: Hall Rental Pricing (Peak vs Off-Peak) + Location Links */}
            <ConicGlowCard
              className="composable-stage-card showcase-bento-primary"
              glowIntensity={0.65}
              ariaLabel={`Calculate slot & hall rental budget for ${currentVenue.name}`}
              onClick={() => onCheckAvailability(currentVenue, flatPeakPkg || venuePackages[0])}
            >
              <div className="showcase-overview-card-inner" style={{ padding: '0.9rem 1rem', display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', gap: '0.45rem' }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.38rem' }}>
                    <span className="pill-badge pricing" style={{ padding: '0.14rem 0.5rem', fontSize: '0.66rem' }}>
                      <SparklesIcon size={11} />
                      <span>Hall Rental Pricing</span>
                    </span>
                    <span className="num-tabular" style={{ fontSize: '0.68rem', fontWeight: 700, color: 'hsl(var(--accent-cyan-text))' }}>
                      {hasExplicitHallRental ? 'Verified Tariff' : 'Area Market Est.'}
                    </span>
                  </div>

                  <h3 className="showcase-bento-heading" style={{ fontSize: '1rem', fontWeight: 800, marginBottom: '0.38rem' }}>
                    Peak &amp; Off-Peak Hall Rental
                  </h3>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.4rem', marginBottom: '0.45rem' }}>
                    <div
                      style={{
                        padding: '0.42rem 0.58rem',
                        borderRadius: '0.6rem',
                        backgroundColor: 'hsl(var(--surface-elevated) / 0.85)',
                        border: '1px solid hsl(var(--glow-gemini-blue) / 0.35)'
                      }}
                    >
                      <div style={{ fontSize: '0.63rem', color: 'hsl(var(--text-secondary))', fontWeight: 600 }}>
                        Peak / Weekend
                      </div>
                      <div className="num-tabular" style={{ fontSize: '0.92rem', fontWeight: 800, color: 'hsl(var(--accent-gold))', marginTop: '0.06rem' }}>
                        {hasExplicitHallRental ? '' : '~'}Rs. {peakHallRentalPkr.toLocaleString()}
                      </div>
                    </div>

                    <div
                      style={{
                        padding: '0.42rem 0.58rem',
                        borderRadius: '0.6rem',
                        backgroundColor: 'hsl(var(--accent-emerald) / 0.1)',
                        border: '1px solid hsl(var(--accent-emerald) / 0.35)'
                      }}
                    >
                      <div style={{ fontSize: '0.63rem', color: 'hsl(var(--accent-emerald-text))', fontWeight: 700 }}>
                        Off-Peak / Weekday
                      </div>
                      <div className="num-tabular" style={{ fontSize: '0.92rem', fontWeight: 800, color: 'hsl(var(--text-primary))', marginTop: '0.06rem' }}>
                        {hasExplicitHallRental ? '' : '~'}Rs. {offPeakHallRentalPkr.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div
                    className="showcase-bento-included"
                    style={{
                      padding: '0.4rem 0.6rem',
                      borderRadius: '0.55rem',
                      backgroundColor: 'hsl(var(--surface-elevated) / 0.75)',
                      border: '1px solid hsl(var(--porcelain-50) / 0.07)',
                      fontSize: '0.7rem',
                      lineHeight: 1.35,
                      color: 'hsl(var(--text-secondary))',
                      marginBottom: '0.45rem'
                    }}
                  >
                    <strong style={{ color: 'hsl(var(--text-primary))' }}>Included:</strong> Main Hall &amp; Stage, Central AC, Standby Generator, Valet Parking &amp;{' '}
                    <strong style={{ color: 'hsl(var(--accent-emerald-text))' }}>Free 72h Slot Hold</strong>.
                  </div>

                  {/* Tucked Location & Contact Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.32rem', flexWrap: 'wrap' }}>
                    <a
                      href={mapsSearchUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="corridor-chip"
                      style={{ textDecoration: 'none', color: 'hsl(var(--text-primary))', padding: '0.18rem 0.5rem', fontSize: '0.65rem' }}
                    >
                      <MapPinIcon size={11} style={{ color: 'hsl(var(--glow-gemini-blue))' }} />
                      <span>Google Maps</span>
                    </a>
                    {verifiedWebsite && (
                      <a
                        href={verifiedWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="corridor-chip"
                        style={{ textDecoration: 'none', color: 'hsl(var(--text-primary))', padding: '0.18rem 0.5rem', fontSize: '0.65rem' }}
                      >
                        <GlobeIcon size={11} style={{ color: 'hsl(var(--glow-gemini-cyan))' }} />
                        <span>Website</span>
                      </a>
                    )}
                    {verifiedPhone && (
                      <a
                        href={`tel:${verifiedPhone}`}
                        className="corridor-chip"
                        style={{ textDecoration: 'none', color: 'hsl(var(--text-primary))', padding: '0.18rem 0.5rem', fontSize: '0.65rem' }}
                      >
                        <PhoneIcon size={11} style={{ color: 'hsl(var(--accent-emerald))' }} />
                        <span>{verifiedPhone}</span>
                      </a>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    borderTop: '1px solid hsl(var(--porcelain-50) / 0.07)',
                    paddingTop: '0.4rem',
                    marginTop: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: 'hsl(var(--accent-cyan-text))'
                  }}
                >
                  <span>Starting Hall Rental • Select Date &amp; Quote</span>
                  <ArrowRightIcon size={12} />
                </div>
              </div>
            </ConicGlowCard>

            {/* EXECUTIVE CARD 2: Architectural Spaces & Stage Photo Card */}
            <ConicGlowCard
              className="composable-stage-card showcase-bento-secondary"
              glowIntensity={0.55}
              ariaLabel={`Open Space & Stage Photo Cards for ${currentVenue.name}`}
              onClick={() => switchFocusView('photos', 0)}
            >
              <div className="showcase-overview-card-inner" style={{ padding: '0.9rem 1rem', display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', gap: '0.45rem' }}>
                <div>
                  <div
                    className="showcase-bento-media"
                    style={{
                      position: 'relative',
                      height: '8.5rem',
                      borderRadius: '0.7rem',
                      overflow: 'hidden',
                      marginBottom: '0.5rem',
                      background: 'linear-gradient(135deg, hsl(var(--obsidian-850)), hsl(var(--obsidian-950)))'
                    }}
                  >
                    <img
                      src={gallerySpaces[0]?.imgUrl || coverUrl}
                      alt={currentVenue.name}
                      decoding="async"
                      draggable={false}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <div className="venue-card-scrim" />
                    <div className="badge-overlay-top-left" style={{ top: '0.45rem', left: '0.45rem' }}>
                      <span className="pill-badge" style={{ fontSize: '0.62rem', padding: '0.12rem 0.42rem' }}>
                        <CameraIcon size={10} />
                        <span>
                          {gallerySpaces.length} {gallerySpaces.length === 1 ? 'Photo' : 'Photos'}
                        </span>
                      </span>
                    </div>
                  </div>

                  <h3 className="showcase-bento-heading" style={{ fontSize: '0.95rem', fontWeight: 800, marginBottom: '0.2rem', lineHeight: 1.22 }}>
                    Spaces &amp; Stage Gallery
                  </h3>
                  <p className="showcase-bento-desc" style={{ fontSize: '0.73rem', color: 'hsl(var(--text-secondary))', lineHeight: 1.38, margin: 0 }}>
                    {gallerySpaces[0]?.desc ||
                      `Inspect verified hall architecture, stage lighting, and guest seating at ${currentVenue.name}.`}
                  </p>
                </div>

                <div
                  style={{
                    borderTop: '1px solid hsl(var(--porcelain-50) / 0.07)',
                    paddingTop: '0.4rem',
                    marginTop: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    color: 'hsl(var(--text-secondary))'
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.28rem' }}>
                    <CameraIcon size={11} style={{ color: 'hsl(var(--glow-gemini-cyan))' }} />
                    <span>All Photos ({gallerySpaces.length})</span>
                  </span>
                  <ArrowRightIcon size={11} />
                </div>
              </div>
            </ConicGlowCard>

            {/* EXECUTIVE CARD 3: Capacity, Electricity/AC Backup & Valet Parking Card */}
            <ConicGlowCard
              className="composable-stage-card showcase-bento-secondary"
              glowIntensity={0.55}
              ariaLabel={`Open Capacity, Backup & Valet Specs for ${currentVenue.name}`}
              onClick={() => switchFocusView('amenities')}
            >
              <div className="showcase-overview-card-inner" style={{ padding: '0.9rem 1rem', display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', gap: '0.45rem' }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem', gap: '0.25rem' }}>
                    <span className="pill-badge verified" style={{ padding: '0.12rem 0.44rem', fontSize: '0.62rem' }}>
                      <ShieldCheckIcon size={10} />
                      <span>Core Specs</span>
                    </span>
                    <span className="num-tabular header-mobile-hide" style={{ fontSize: '0.68rem', fontWeight: 700, color: 'hsl(var(--text-muted))' }}>
                      {currentVenue.minCapacity}–{currentVenue.maxCapacity} Pax
                    </span>
                  </div>

                  <h3 className="showcase-bento-heading" style={{ fontSize: '0.95rem', fontWeight: 800, marginBottom: '0.32rem', lineHeight: 1.22 }}>
                    Capacity &amp; Power Backup
                  </h3>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.28rem' }}>
                    <div className="showcase-bento-spec-pill" style={{ padding: '0.32rem 0.5rem', borderRadius: '0.48rem', backgroundColor: 'hsl(var(--surface-elevated) / 0.7)', fontSize: '0.68rem', lineHeight: 1.28 }}>
                      <strong style={{ color: 'hsl(var(--glow-gemini-blue))' }}>Capacity:</strong> {currentVenue.minCapacity}–{currentVenue.maxCapacity} Pax
                    </div>
                    <div className="showcase-bento-spec-pill" style={{ padding: '0.32rem 0.5rem', borderRadius: '0.48rem', backgroundColor: 'hsl(var(--surface-elevated) / 0.7)', fontSize: '0.68rem', lineHeight: 1.28 }}>
                      <strong style={{ color: 'hsl(var(--accent-emerald))' }}>Backup:</strong> Generator + Central AC
                    </div>
                    <div className="showcase-bento-spec-pill" style={{ padding: '0.32rem 0.5rem', borderRadius: '0.48rem', backgroundColor: 'hsl(var(--surface-elevated) / 0.7)', fontSize: '0.68rem', lineHeight: 1.28 }}>
                      <strong style={{ color: 'hsl(var(--accent-gold))' }}>Parking:</strong> Valet &amp; Drop-Off
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    borderTop: '1px solid hsl(var(--porcelain-50) / 0.07)',
                    paddingTop: '0.4rem',
                    marginTop: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    color: 'hsl(var(--text-secondary))'
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.28rem' }}>
                    <CheckIcon size={11} style={{ color: 'hsl(var(--accent-emerald))' }} />
                    <span>All Specs (3)</span>
                  </span>
                  <ArrowRightIcon size={11} />
                </div>
              </div>
            </ConicGlowCard>
          </div>
        </div>
      )}

      {/* Lightbox Modal for Gallery Photo Inspection (Supports Horizontal Swipe & Prev/Next Stepper) */}
      {lightboxItem && (
        <div className="modal-backdrop" onClick={() => setLightboxItem(null)}>
          <div
            className="modal-content"
            style={{ maxWidth: '52rem', padding: '1.25rem', touchAction: 'pan-y' }}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.875rem', gap: '0.5rem' }}>
              <div style={{ minWidth: 0 }}>
                <span className="pill-badge verified" style={{ marginRight: '0.5rem' }}>
                  {lightboxItem.source}
                </span>
                <span className="pill-badge gemini num-tabular" style={{ marginRight: '0.5rem' }}>
                  {safePhotoIndex + 1} / {filteredGallery.length}
                </span>
                <strong style={{ fontSize: '1.05rem' }}>
                  {currentVenue.name} — {lightboxItem.label}
                </strong>
              </div>
              <button
                type="button"
                className="dock-action-btn"
                onClick={() => setLightboxItem(null)}
                aria-label="Close Lightbox"
              >
                <CloseIcon size={16} />
              </button>
            </div>
            <div style={{ position: 'relative', height: '26rem', borderRadius: '0.875rem', overflow: 'hidden', backgroundColor: 'hsl(var(--obsidian-950))' }}>
              <img
                src={lightboxItem.imgUrl}
                alt={lightboxItem.label}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <div
                style={{
                  position: 'absolute',
                  inset: 'auto 0.75rem 0.75rem 0.75rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  pointerEvents: 'none'
                }}
              >
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={stepShowcaseBackward}
                  style={{ pointerEvents: 'auto', padding: '0.32rem 0.65rem', fontSize: '0.72rem', gap: '0.28rem' }}
                >
                  <ArrowLeftIcon size={12} />
                  <span>{safePhotoIndex === 0 ? 'Overview' : 'Prev Photo'}</span>
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={stepShowcaseForward}
                  style={{ pointerEvents: 'auto', padding: '0.32rem 0.65rem', fontSize: '0.72rem', gap: '0.28rem' }}
                >
                  <span>{safePhotoIndex >= filteredGallery.length - 1 ? 'Specs' : 'Next Photo'}</span>
                  <ArrowRightIcon size={12} />
                </button>
              </div>
            </div>
            <p style={{ fontSize: '0.85rem', color: 'hsl(var(--text-secondary))', marginTop: '0.875rem' }}>
              {lightboxItem.desc}
            </p>
          </div>
        </div>
      )}
    </div>
  );
});
